/**
 * Real font metrics for headless text measurement.
 *
 * Backed by the Adobe Core-14 AFM advance-width tables (Helvetica,
 * Helvetica-Bold, Courier) — the same standard metrics PDF renderers use.
 * Widths are in 1/1000 em; a glyph's pixel advance is `WX/1000 * size`.
 * This is deterministic and dependency-free, so Node and the browser agree
 * on layout without shipping a font binary.
 *
 * The board's CSS font stack starts with `system-ui` (sans) / `ui-monospace`
 * (mono). Helvetica/Courier metrics are a faithful proxy for that stack's
 * common resolutions (Arial/Helvetica, Menlo/Courier) — close enough that
 * the SVG matches the DOM within a glyph or two; exact parity per-platform
 * is the browser's `canvasMeasure` job (see canvas.ts).
 *
 * Cyrillic band (U+0400..U+04FF) is derived from Inter — the font
 * `@archlang/render` actually rasterises with (packages/render/fonts/Inter_*.ttf).
 * Adobe's Core-14 Helvetica AFM is Latin-only (StandardEncoding has no Cyrillic
 * glyphs), so there is no Helvetica Cyrillic width to copy; Inter is the exact
 * render font (making these widths precise, not a proxy, for PNG/SVG export) and
 * its Latin advances sit in the same proportional family as Helvetica's (space
 * 281 vs 278, m 876 vs 833), so mixed Latin+Cyrillic labels stay seamless.
 * Advances were extracted from Inter's hmtx (normalised from UPM 2048 to 1/1000
 * em); regular = Inter Regular (400), bold = Inter Bold (700), matching the two
 * weight buckets the Helvetica tables already use.
 */
import type { FontSpec } from "./model.js";
import type { Measure, TextMetrics } from "./text.js";

// Adobe Core-14 AFM WX values, ASCII 32..126. Index = charCode - 32.
// prettier-ignore
const HELVETICA = [
  278,278,355,556,556,889,667,191,333,333,389,584,278,333,278,278,
  556,556,556,556,556,556,556,556,556,556,278,278,584,584,584,556,
  1015,667,667,722,722,667,611,778,722,278,500,667,556,833,722,778,
  667,778,722,667,611,722,667,944,667,667,611,278,278,278,469,556,
  333,556,556,500,556,556,278,556,556,222,222,500,222,833,556,556,
  556,556,333,500,278,556,500,722,500,500,500,334,260,334,584,
];
// prettier-ignore
const HELVETICA_BOLD = [
  278,333,474,556,556,889,722,238,333,333,389,584,278,333,278,278,
  556,556,556,556,556,556,556,556,556,556,333,333,584,584,584,611,
  975,722,722,722,722,667,611,778,722,278,556,722,611,833,722,778,
  667,778,722,667,611,722,667,944,667,667,611,333,278,333,584,556,
  333,556,611,556,611,556,333,611,611,278,278,556,278,889,611,611,
  611,611,389,556,333,611,556,778,556,556,500,389,280,389,584,
];

// Inter WX values, Cyrillic block U+0400..U+04FF. Index = codePoint - 0x0400.
// -1 = no glyph in Inter (archaic letters) → falls through to DEFAULT_WX; 0 =
// Cyrillic combining mark (zero advance). Covers the core alphabet, Ё/ё and the
// extension letters Inter ships. See the header note for provenance.
// prettier-ignore
const CYRILLIC = [
  601,601,841,579,730,642,269,269,571,1113,1125,884,672,753,628,722,
  690,646,654,579,819,601,960,618,753,753,672,748,903,743,765,743,
  639,730,646,628,828,682,721,700,958,972,816,883,649,730,1015,644,
  562,594,565,436,613,583,810,494,591,591,542,575,770,588,600,588,
  612,571,466,562,687,546,597,578,822,837,644,736,559,571,843,554,
  583,583,591,436,571,528,242,242,242,924,896,591,542,591,562,588,
  896,807,688,615,956,782,690,571,937,776,853,694,1145,958,544,522,
  880,778,765,600,704,593,704,593,1327,1142,-1,-1,-1,-1,-1,-1,
  710,565,632,0,0,0,0,0,0,0,753,591,688,615,635,609,
  579,436,579,436,652,542,979,830,618,494,706,562,667,585,-1,-1,
  839,627,758,603,985,677,1108,922,985,778,730,571,646,466,679,562,
  679,562,702,565,916,659,715,592,700,578,700,591,839,714,839,714,
  269,960,810,683,587,748,575,743,588,743,588,715,592,903,770,242,
  690,562,690,562,994,917,601,583,746,583,746,583,960,810,618,494,
  620,620,753,591,753,591,765,600,765,600,765,600,730,571,628,562,
  628,562,628,562,700,578,579,436,883,736,592,436,702,521,682,546,
];
// prettier-ignore
const CYRILLIC_BOLD = [
  607,607,858,583,740,655,281,281,584,1118,1126,895,719,762,694,727,
  747,647,662,583,868,607,1068,646,762,762,719,750,932,747,771,747,
  648,740,667,694,835,738,767,708,989,1017,830,918,663,740,1042,657,
  581,609,577,453,661,596,864,519,623,623,583,604,812,622,613,613,
  630,588,510,602,777,580,656,601,860,888,678,807,590,589,867,578,
  596,596,623,453,589,560,271,271,271,944,924,623,583,623,602,613,
  978,881,710,644,1002,830,747,591,992,833,883,747,1184,1022,571,543,
  894,830,771,613,758,618,758,618,1372,1197,-1,-1,-1,-1,-1,-1,
  718,586,668,0,0,0,0,0,0,0,762,623,710,644,659,611,
  583,453,583,453,664,574,1104,897,646,519,761,616,719,634,-1,-1,
  886,672,775,649,1004,709,1128,939,1046,839,740,588,667,510,731,600,
  731,600,773,613,951,711,735,629,708,601,708,623,890,754,890,754,
  281,1068,864,694,622,750,604,747,622,747,622,735,629,932,812,271,
  747,581,747,581,1022,910,607,596,748,596,748,596,1068,864,646,519,
  646,646,762,623,762,623,771,613,771,613,771,613,740,589,694,602,
  694,602,694,602,708,601,583,453,918,807,620,453,790,541,738,580,
];

const COURIER_WX = 600; // monospace: every glyph is 600/1000 em
const DEFAULT_WX = 600; // non-ASCII fallback (avg-ish)

function isMono(family: string): boolean {
  return /mono|courier|menlo|consolas/i.test(family);
}

const COMBINING = /\p{M}/u; // zero-advance marks

/** True for East-Asian Wide / Fullwidth / emoji code points (~1em advance).
 *  Emoji band matches the live view's `glyphWidth` visual-width model —
 *  browsers paint emoji at ≥1em, so the 0.6em fallback under-measures ~2×
 *  and pre-wrapped nowrap lines would overflow their block. */
function isFullWidth(cp: number): boolean {
  return (
    (cp >= 0x1100 && cp <= 0x115f) || // Hangul Jamo
    (cp >= 0x2e80 && cp <= 0x303e) || // CJK radicals … symbols
    (cp >= 0x3041 && cp <= 0x33ff) || // kana, CJK symbols
    (cp >= 0x3400 && cp <= 0x4dbf) || // CJK ext A
    (cp >= 0x4e00 && cp <= 0x9fff) || // CJK unified
    (cp >= 0xa000 && cp <= 0xa4cf) || // Yi
    (cp >= 0xac00 && cp <= 0xd7a3) || // Hangul syllables
    (cp >= 0xf900 && cp <= 0xfaff) || // CJK compat
    (cp >= 0xfe30 && cp <= 0xfe4f) || // CJK compat forms
    (cp >= 0xff00 && cp <= 0xff60) || // fullwidth forms
    (cp >= 0xffe0 && cp <= 0xffe6) ||
    (cp >= 0x1f000 && cp <= 0x1faff) || // emoji + symbol blocks
    (cp >= 0x20000 && cp <= 0x3fffd) // CJK ext B+ (astral)
  );
}

function advanceCp(cp: number, family: string, weight: number): number {
  if (isMono(family)) return COURIER_WX;
  const idx = cp - 32;
  const table = weight >= 600 ? HELVETICA_BOLD : HELVETICA;
  if (idx >= 0 && idx < table.length) return table[idx]!;
  // Non-ASCII: a combining mark advances 0; Cyrillic reads its own band; an
  // East-Asian wide glyph is ~1em; any other script gets the avg fallback.
  // (Iterate by CODE POINT so an astral glyph counts once, not once per surrogate.)
  if (COMBINING.test(String.fromCodePoint(cp))) return 0;
  const cyr = cp - 0x0400;
  if (cyr >= 0 && cyr < CYRILLIC.length) {
    const wx = (weight >= 600 ? CYRILLIC_BOLD : CYRILLIC)[cyr]!;
    if (wx >= 0) return wx; // -1 = no glyph in Inter → fall through to fallback
  }
  if (isFullWidth(cp)) return 1000;
  return DEFAULT_WX;
}

export function measureWidth(s: string, font: FontSpec): number {
  let units = 0;
  for (const ch of s) units += advanceCp(ch.codePointAt(0)!, font.family, font.weight);
  return (units / 1000) * font.size;
}

/** Line height as a multiple of font size — the ONE value every consumer
 *  (text metrics, SVG line stacking, figure caption bands) must agree on, or
 *  the two backends stack lines at different heights. */
export const LINE_HEIGHT = 1.16;

export const metricsMeasure: Measure = {
  text(s, font): TextMetrics {
    return {
      width: measureWidth(s, font),
      // Helvetica AFM: Ascender 718, Descender -207.
      ascent: font.size * 0.718,
      descent: font.size * 0.207,
      lineHeight: font.size * LINE_HEIGHT,
    };
  },
};
